import React, { useState } from "react";
import { FileText, ShoppingBag, Shirt, QrCode, Download, ExternalLink, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORIGIN = process.env.REACT_APP_BACKEND_URL;

/**
 * PrintablesPanel — Admin › Printables
 *
 * Three tiles for the owner: Menu, Takeaway Pamphlet, Merch Sheet.
 * Each renders a live thumbnail (via iframe), a download button, and a
 * public shareable URL. Files also live at /downloads/... for direct linking.
 */
const PRINTABLES = [
  {
    key: "menu",
    label: "Full Menu",
    icon: FileText,
    tagline: "Double-sided A4 · food front, drinks & info back · auto-generated from live prices",
    apiUrl: `${API}/menu.pdf`,
    publicUrl: `${ORIGIN}/downloads/menu/Mamamina-Soulfeed-Menu.pdf`,
    filename: "Mamamina-Soulfeed-Menu.pdf",
    accent: "from-amber-500/20 to-transparent",
  },
  {
    key: "qr_poster",
    label: "Menu QR Poster",
    icon: QrCode,
    tagline: "Huge A3 poster · scannable from across the room · window-ready",
    apiUrl: `${API}/menu-qr-poster.pdf`,
    publicUrl: `${ORIGIN}/downloads/menu/Mamamina-Menu-QR-Poster-A3.pdf`,
    filename: "Mamamina-Menu-QR-Poster-A3.pdf",
    accent: "from-yellow-500/20 to-transparent",
  },
  {
    key: "takeaway",
    label: "Takeaway Pamphlet",
    icon: ShoppingBag,
    tagline: "Fold-up 3-panel A4 · QR to menu · address + hours",
    apiUrl: `${API}/takeaway-pamphlet.pdf`,
    publicUrl: `${ORIGIN}/downloads/takeaway/Mamamina-Takeaway-Pamphlet.pdf`,
    filename: "Mamamina-Takeaway-Pamphlet.pdf",
    accent: "from-emerald-500/20 to-transparent",
  },
  {
    key: "merch",
    label: "Merchandise Sheet",
    icon: Shirt,
    tagline: "Real product photos · tees · aprons · mugs · caps · A4",
    apiUrl: `${API}/merch-sheet.pdf`,
    publicUrl: `${ORIGIN}/downloads/merchandise/Mamamina-Merch-Sheet.pdf`,
    filename: "Mamamina-Merch-Sheet.pdf",
    accent: "from-purple-500/20 to-transparent",
  },
];

export default function PrintablesPanel() {
  // Cache-buster keeps the iframe fresh after a re-generate
  const [nonce, setNonce] = useState(Date.now());

  const download = (apiUrl, filename) => {
    // Force a download (Content-Disposition already inline, but we want to save)
    const link = document.createElement("a");
    link.href = apiUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const regen = () => setNonce(Date.now());

  return (
    <section data-testid="printables-panel" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FileText size={16} className="text-[var(--mm-accent)]" />
          <h2 className="section-label">Printables · Menu · Takeaway · Merch</h2>
        </div>
        <button
          onClick={regen}
          data-testid="printables-regen"
          className="inline-flex items-center gap-1.5 border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-xs tracking-[0.2em] uppercase hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors"
        >
          <RefreshCw size={12} /> Re-generate
        </button>
      </div>

      <p className="text-xs text-[var(--mm-cream-muted)] max-w-2xl leading-relaxed">
        Three print-ready PDFs the kitchen can hand off to any printer. Every
        file also lives at a public URL — put it in your Instagram bio, email
        signature, or QR poster.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PRINTABLES.map((p) => {
          const Icon = p.icon;
          const previewUrl = `${p.apiUrl}?t=${nonce}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`;
          return (
            <div
              key={p.key}
              data-testid={`printable-card-${p.key}`}
              className="relative border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]/40 overflow-hidden"
            >
              {/* Accent glow */}
              <div className={`pointer-events-none absolute inset-0 bg-gradient-to-b ${p.accent}`} />

              {/* Preview */}
              <div className="relative aspect-[3/4] bg-[#0a0a0a] border-b border-[var(--mm-line)]">
                <iframe
                  src={previewUrl}
                  title={`Preview ${p.label}`}
                  className="w-full h-full pointer-events-none relative z-[1]"
                  data-testid={`printable-preview-${p.key}`}
                />
                {/* Fallback shown when browser can't render an inline PDF */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                  <Icon size={48} className="text-[var(--mm-accent)]/30" />
                </div>
                <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/40 to-transparent pointer-events-none z-[2]" />
              </div>

              {/* Details */}
              <div className="relative p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <Icon size={18} className="text-[var(--mm-accent)] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display italic text-xl text-[var(--mm-cream)] leading-tight">
                      {p.label}
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--mm-cream-muted)] mt-1 leading-relaxed">
                      {p.tagline}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={() => download(p.apiUrl, p.filename)}
                    data-testid={`printable-download-${p.key}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[var(--mm-accent)] text-[var(--mm-bg)] px-3 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:opacity-90 transition-opacity"
                  >
                    <Download size={12} /> Download
                  </button>
                  <a
                    href={p.publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`printable-share-${p.key}`}
                    className="inline-flex items-center gap-1.5 border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-xs tracking-[0.2em] uppercase hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors"
                    title="Open the public shareable URL"
                  >
                    <ExternalLink size={12} /> Share
                  </a>
                </div>

                <div className="pt-2 text-[10px] font-mono text-[var(--mm-cream-muted)] opacity-70 break-all leading-relaxed">
                  {p.publicUrl.replace(/^https?:\/\//, "")}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-[var(--mm-line)] pt-6 text-xs text-[var(--mm-cream-muted)] leading-relaxed max-w-2xl">
        <span className="font-semibold text-[var(--mm-cream)] tracking-[0.15em] uppercase text-[10px]">
          Pro-tip ·
        </span>{" "}
        The Menu is regenerated on demand from your live database — change a
        price in Menu tab and it updates the PDF automatically. The Merch
        Sheet catalogue lives in the code at{" "}
        <code className="text-[var(--mm-accent)]">backend/merch_sheet_pdf.py</code>{" "}
        — ping the dev to swap products.
      </div>
    </section>
  );
}
